import debugx from "debugx";

const debug = debugx("bikedash:wakelock");

export type WakeLockStatus =
  | "active"
  | "needs-gesture"
  | "disabled"
  | "unsupported";

class WakeLockService {
  private static instance: WakeLockService;
  private wakeLock: any = null;
  private enabled = false;
  private status: WakeLockStatus = "disabled";
  private callbacks = new Set<(status: WakeLockStatus) => void>();
  private needsGesture = false;
  private gestureDetected = false;
  private reminderTimer: NodeJS.Timeout | null = null;

  private constructor() {
    this.setupVisibilityListener();
  }

  static getInstance(): WakeLockService {
    if (!WakeLockService.instance) {
      WakeLockService.instance = new WakeLockService();
    }
    return WakeLockService.instance;
  }

  private setupVisibilityListener() {
    document.addEventListener("visibilitychange", async () => {
      if (document.visibilityState === "visible" && this.enabled) {
        debug("Page became visible - wake lock needs to be re-acquired");
        this.needsGesture = true;
        this.updateStatus("needs-gesture");
      }
    });
  }

  async enable() {
    debug("Enabling wake lock");
    this.enabled = true;

    if (!("wakeLock" in navigator)) {
      debug.warn("Wake Lock API not supported");
      this.updateStatus("unsupported");
      return;
    }

    // Try to acquire immediately, but may need gesture
    await this.acquire(true); // Pass true to indicate initial load
  }

  disable() {
    debug("Disabling wake lock");
    this.enabled = false;
    this.release();
    this.updateStatus("disabled");

    // Clear reminder timer if active
    if (this.reminderTimer) {
      clearTimeout(this.reminderTimer);
      this.reminderTimer = null;
    }
  }

  async handleUserGesture() {
    // Mark that user has interacted
    this.gestureDetected = true;

    // Clear reminder timer if active
    if (this.reminderTimer) {
      clearTimeout(this.reminderTimer);
      this.reminderTimer = null;
      debug("Cancelled 10-second reminder timer");
    }

    // Called on first tap/interaction after visibility change or when needs gesture
    if (!this.enabled || !this.needsGesture) {
      return;
    }

    debug("User gesture detected - acquiring wake lock");
    await this.acquire(false);
  }

  private async acquire(isInitialLoad: boolean = false) {
    if (!("wakeLock" in navigator)) {
      this.updateStatus("unsupported");
      return;
    }

    try {
      this.wakeLock = await navigator.wakeLock.request("screen");
      this.needsGesture = false;
      debug("Wake lock acquired successfully");

      // Listen for release
      this.wakeLock.addEventListener("release", () => {
        debug("Wake lock was released");
        if (this.enabled) {
          this.needsGesture = true;
          this.updateStatus("needs-gesture");
        }
      });

      // Delay hiding the indicator by 200ms to prevent accidental clicks on elements below
      setTimeout(() => {
        this.updateStatus("active");
        debug("Wake lock indicator hidden after delay");
      }, 200);
    } catch (err: any) {
      debug.error("Failed to acquire wake lock:", err);
      if (err.name === "NotAllowedError") {
        debug("Wake lock needs user gesture");
        this.needsGesture = true;

        // On initial load, wait 10 seconds before showing indicator
        if (isInitialLoad) {
          debug("Starting 10-second reminder timer before showing indicator");
          this.reminderTimer = setTimeout(() => {
            if (!this.gestureDetected && this.needsGesture) {
              debug("10 seconds elapsed with no gesture - showing indicator");
              this.updateStatus("needs-gesture");
            }
          }, 10000);
        } else {
          // Show indicator immediately for visibility changes
          this.updateStatus("needs-gesture");
        }
      }
    }
  }

  private release() {
    if (this.wakeLock) {
      this.wakeLock.release();
      this.wakeLock = null;
      debug("Wake lock released");
    }
  }

  private updateStatus(status: WakeLockStatus) {
    if (this.status === status) return;

    this.status = status;
    debug("Wake lock status changed to:", status);
    this.notifySubscribers();
  }

  getStatus(): WakeLockStatus {
    return this.status;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  subscribe(callback: (status: WakeLockStatus) => void): () => void {
    this.callbacks.add(callback);
    // Call immediately with current status
    callback(this.status);
    return () => {
      this.callbacks.delete(callback);
    };
  }

  private notifySubscribers() {
    this.callbacks.forEach((callback) => callback(this.status));
  }
}

export const wakeLockService = WakeLockService.getInstance();
